import { useEffect, useRef, useState } from "react";
import { Loader2, Lock, MapPin, Navigation, Search } from "lucide-react";
import { toast } from "sonner";
import { GROUNDY } from "@/components/onboarding/groundyArt";
import { OButton, OC, ScreenFrame, StepHeader, StepHeading, StepLine, TextLink } from "@/components/onboarding/onboardingUi";
import { LOCATION_COPY } from "@/lib/permissionCopy";
import { getGeolocationErrorMessage } from "@/lib/locationDetection";
import {
  detectCurrentLocation,
  isPermissionDenied,
  resolveRegionPrediction,
  searchRegions,
  type HomeLocation,
  type PlacePrediction,
} from "@/lib/homeLocation";

/** Decorative map, drawn with plain shapes so nothing extra loads. */
function MiniMap() {
  return (
    <div
      aria-hidden="true"
      className="relative w-full h-[150px] rounded-2xl overflow-hidden mt-5"
      style={{ backgroundColor: "#EDE4D6" }}
    >
      <div className="absolute inset-0 grid grid-cols-4 grid-rows-3 gap-[6px] p-[6px]">
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={i} className="rounded-md" style={{ backgroundColor: i === 5 || i === 10 ? "#DCE5D2" : "#E6DCCB" }} />
        ))}
      </div>
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-12 w-12 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(59,35,20,0.12)" }}>
        <div className="h-3.5 w-3.5 rounded-full" style={{ backgroundColor: OC.fill }} />
      </div>
    </div>
  );
}

export function LocationStep({ onDone }: { onDone: (loc: HomeLocation | null, method: "gps" | "search" | "skipped") => void }) {
  const [query, setQuery] = useState("");
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDetecting, setIsDetecting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (query.trim().length < 2) { setPredictions([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      setIsSearching(true);
      try {
        const results = await searchRegions(query);
        if (!cancelled) setPredictions(results);
      } catch { /* silent, like the location pop up */ }
      finally { if (!cancelled) setIsSearching(false); }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);

  const pick = async (p: PlacePrediction) => {
    setIsSaving(true);
    try {
      const loc = await resolveRegionPrediction(p);
      onDone(loc, "search");
    } catch {
      toast.error("We couldn't set that area. Try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const detect = async () => {
    setIsDetecting(true);
    try {
      const loc = await detectCurrentLocation();
      onDone(loc, "gps");
    } catch (error) {
      if (isPermissionDenied(error)) {
        toast.error("Location is off for Grounded.", { description: "Search for your area above instead." });
        inputRef.current?.focus();
      } else {
        const { title, description } = getGeolocationErrorMessage(error);
        toast.error(title, { description });
      }
    } finally {
      setIsDetecting(false);
    }
  };

  return (
    <ScreenFrame
      testId="location-step"
      dock={
        <>
          <OButton onClick={detect} disabled={isDetecting || isSaving}>
            {isDetecting ? <Loader2 size={18} className="animate-spin" /> : <><Navigation size={16} />{LOCATION_COPY.detectCta}</>}
          </OButton>
          <TextLink onClick={() => onDone(null, "skipped")}>Not now</TextLink>
        </>
      }
    >
      <StepHeader groundy={GROUNDY.location} eyebrow="Your area" />
      <StepHeading>{LOCATION_COPY.heading}</StepHeading>
      <StepLine>So the cafés we show you first are close by.</StepLine>
      <MiniMap />
      <div className="relative mt-4">
        <Search size={16} color={OC.muted} className="absolute left-[14px] top-1/2 -translate-y-1/2" />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by city, neighborhood, or zip..."
          aria-label="Search for your area"
          className="w-full h-12 rounded-xl outline-none"
          style={{ backgroundColor: OC.white, border: `0.5px solid ${OC.divider}`, padding: "0 40px 0 40px", fontSize: 16, color: OC.text }}
        />
        {(isSearching || isSaving) && <Loader2 size={16} className="absolute right-[14px] top-1/2 -translate-y-1/2 animate-spin" color={OC.muted} />}
        {predictions.length > 0 && (
          <div className="absolute z-20 w-full mt-1 rounded-xl overflow-hidden shadow-lg max-h-48 overflow-y-auto" style={{ backgroundColor: OC.white, border: `0.5px solid ${OC.divider}` }}>
            {predictions.map((p) => (
              <button
                key={p.place_id}
                onClick={() => pick(p)}
                disabled={isSaving}
                className="w-full px-3 py-2.5 text-left flex items-center gap-2 text-sm"
                style={{ color: OC.text }}
              >
                <MapPin size={14} color={OC.muted} className="shrink-0" />
                <span>{p.description}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <p className="flex items-start gap-2 mt-3 px-1" style={{ fontSize: 12, color: OC.textSec }}>
        <Lock size={12} className="mt-0.5 shrink-0" />
        <span>{LOCATION_COPY.why}</span>
      </p>
    </ScreenFrame>
  );
}
